<template>
    <div
        ref="card"
        class="kpi-card"
        :style="{ padding: normalizeSize(17, true) + ' ' + normalizeSize(20, true) }"
        :class="{ 'with-weather': showWeather }"
    >
        <div class="kpi-content">
            <div class="kpi-label" :style="{ fontSize: normalizeFontSize(14) }">
                {{ label }}
                <Tooltip v-if="tooltip" :text="tooltip" position="top" wrap light align="center">
                    <span class="kpi-tooltip-icon"><Info :size="14" /></span>
                </Tooltip>
            </div>
            <div class="kpi-value-row">
                <span class="!ppv2-text-white poppinsSemiBold" :style="{ fontSize: normalizeFontSize(17) }">{{
                    formattedValue
                }}</span>
                <div
                    v-if="variation !== null && false"
                    class="kpi-variation ppv2-px-1 ppv2-rounded-lg ppv2-text-xs"
                    :class="variationClass"
                >
                    {{ variationText }}
                </div>
            </div>
            <div v-if="subtitle" class="kpi-subtitle">{{ subtitle }}</div>
        </div>
        <div class="kpi-icon" :style="{ background: iconBg }">
            <slot name="icon">
                <v-icon color="white" size="24">{{ icon }}</v-icon>
            </slot>
        </div>
        <div v-if="showWeather && weather" class="weather-info">
            <span class="weather-temp">{{ weather.temp }}°</span>
            <img :src="weather.icon" :alt="weather.condition" class="weather-icon" />
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import Tooltip from '../../../Shared/Tooltip.vue'
import { Info } from 'lucide-vue'
export default {
    name: 'KpiCard',
    components: {
        Tooltip,
        Info,
    },
    props: {
        label: {
            type: String,
            required: true,
        },
        value: {
            type: [Number, String],
            required: true,
        },
        format: {
            type: String,
            default: 'number', // 'number', 'currency', 'percent'
        },
        currency: {
            type: String,
            default: () => window.symbolCurrency || '€',
        },
        variation: {
            type: Number,
            default: null,
        },
        subtitle: {
            type: String,
            default: '',
        },
        tooltip: {
            type: String,
            default: '',
        },
        icon: {
            type: String,
            default: 'mdi-chart-line',
        },
        iconBg: {
            type: String,
            default: '#7D2DBD',
        },
        showWeather: {
            type: Boolean,
            default: false,
        },
        weather: {
            type: Object,
            default: null,
        },
    },
    computed: {
        formattedValue() {
            if (this.value === undefined || this.value === null) return '0'
            if (typeof this.value === 'string') return this.value

            switch (this.format) {
                case 'currency':
                    return `${this.formatNumber(this.value)}${this.currency}`
                case 'percent':
                    return `${this.value}%`
                default:
                    return this.formatNumber(this.value)
            }
        },
        variationClass() {
            if (this.variation === null) return ''
            return this.variation >= 0 ? 'positive' : 'negative'
        },
        variationText() {
            if (this.variation === null) return ''
            const sign = this.variation >= 0 ? '+' : ''
            return `${sign}${this.variation}%`
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        formatNumber(num) {
            if (num === undefined || num === null) return '0,00'
            return num.toLocaleString(this.$currencyLocale?.replace('_', '-') || 'fr-FR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
    },
}
</script>

<style scoped>
.kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 19px;
    background: linear-gradient(127deg, rgba(6, 11, 38, 0.74) 28.26%, rgba(26, 31, 55, 0.5) 91.2%);
    backdrop-filter: blur(57px);
    min-width: 240px;
    flex: 1;
}

@media (max-width: 640px) {
    .kpi-card {
        min-width: unset;
        padding: 12px 15px !important;
    }
}

.kpi-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.kpi-label {
    color: #a0aec0;
    display: flex;
    align-items: center;
    gap: 5px;
}

.kpi-tooltip-icon {
    cursor: pointer;
    color: #a0aec0;
    font-size: 12px;
    display: inline-flex;
    line-height: 1;
}

.kpi-tooltip-icon:hover {
    color: #fff;
}

.kpi-value-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.kpi-variation {
    display: flex;
    align-items: center;
    gap: 4px;
}

.variation-badge {
    padding: 2px 8px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 400;
}

.kpi-variation.positive {
    background: #00452c;
    color: #00d589;
}

.kpi-variation.negative {
    background: #37242f;
    color: #c52b21;
}

.kpi-variation.neutral {
    background: rgba(255, 255, 255, 0.1);
    color: #a0aec0;
}

.variation-label {
    color: #a0aec0;
    font-size: 12px;
}

.kpi-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.kpi-icon {
    width: 43px;
    height: 43px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 3px 5px rgba(0, 0, 0, 0.02);
}

.kpi-icon :deep(svg),
.kpi-icon svg {
    width: 24px !important;
    height: 24px !important;
}

.weather-container {
    display: flex;
    align-items: center;
    gap: 4px;
}

.weather-temp {
    color: #fff;
    font-size: 18px;
    font-weight: 600;
}

.weather-icon {
    width: 32px;
    height: 32px;
}
</style>
